/**
 * Other Side — Brand mark.
 *
 * The official O.S. gold signature. Used as a constant identity anchor
 * across header, landing, auth, and key emotional moments.
 *
 * Animations:
 *   • Always-on "breathing" — 12-second scale/opacity cycle, synced
 *     period with AmbientAudio's LFO so the mark exhales on the same
 *     rhythm as the soundscape. Invisible without comparison, felt with it.
 *   • Optional "glow-active" — gold drop-shadow pulse that layers on top
 *     when the ambient atmosphere is playing. Gives the logo a literal
 *     extra light when sound is alive, removes it on silence.
 *
 * Sync is achieved via a lightweight custom DOM event:
 *   • `ambientaudio:started` — fired by AmbientAudio on fade-in complete
 *   • `ambientaudio:stopped` — fired on fade-out
 * No prop drilling, no context: any BrandMark mounted anywhere in the
 * tree listens and reacts. Zero coupling with the audio module.
 *
 * Props:
 *   size        — pixel HEIGHT of the mark (default 32, was 28 → +14%)
 *   breathing   — gentle opacity pulse (default true)
 *   ceremonial  — subtle glow for hero moments (default false)
 *   watermark   — low-opacity decorative variant
 *   className   — extra classes
 */
import { useEffect, useState } from "react";

// 764 × 400 — natural aspect ratio of the mark
const MARK_ASPECT = 764 / 400;
const MARK_SRC = "/brand/os-mark.png";
const MONOGRAM_SRC = "/brand/os-monogram.png";

// Module-level state: if the app audio started before THIS BrandMark
// mounts, we still want the glow on. We cache the last seen phase.
let currentAudioActive = false;
if (typeof window !== "undefined") {
    window.addEventListener("ambientaudio:started", () => { currentAudioActive = true; });
    window.addEventListener("ambientaudio:stopped", () => { currentAudioActive = false; });
}

export default function BrandMark({
    size = 32,
    breathing = true,
    ceremonial = false,
    watermark = false,
    monogram = false, // square 1:1 variant for avatars / small UI
    // Legacy props kept to avoid DOM warnings; no-op now.
    glow,       // eslint-disable-line no-unused-vars
    className = "",
    ...rest
}) {
    const h = size;
    const w = monogram ? size : Math.round(size * MARK_ASPECT);
    const src = monogram ? MONOGRAM_SRC : MARK_SRC;

    // Audio-synced glow state. Starts with whatever the module cache knows.
    const [glowActive, setGlowActive] = useState(() => currentAudioActive);

    useEffect(() => {
        if (!breathing) return; // a non-breathing logo never glows either
        const on = () => setGlowActive(true);
        const off = () => setGlowActive(false);
        window.addEventListener("ambientaudio:started", on);
        window.addEventListener("ambientaudio:stopped", off);
        // Resync in case the cache changed between mount and effect
        setGlowActive(currentAudioActive);
        return () => {
            window.removeEventListener("ambientaudio:started", on);
            window.removeEventListener("ambientaudio:stopped", off);
        };
    }, [breathing]);

    if (watermark) {
        return (
            <img
                src={src}
                alt=""
                width={w}
                height={h}
                className={`${className} select-none pointer-events-none`}
                style={{ opacity: 0.08, height: h, width: w }}
                aria-hidden
                {...rest}
            />
        );
    }

    const breatheClass = breathing
        ? (ceremonial ? "brand-breathe-strong" : "brand-breathe")
        : "";
    const glowClass = breathing && glowActive ? "brand-glow-active" : "";

    return (
        <img
            src={src}
            alt="Other Side"
            width={w}
            height={h}
            className={`${className} ${breatheClass} ${glowClass} select-none`}
            style={{ height: h, width: w, display: "block" }}
            draggable={false}
            data-glow={glowActive ? "on" : "off"}
            {...rest}
        />
    );
}
